import { assembleProcessFlow, resolveStepIdFromNodeId } from "../core/builder";
import { getNodeDimensions } from "../core/layout";
import { NODE_WIDTH, SWIMLANE_GAP, SWIMLANE_LABEL_WIDTH, SWIMLANE_MIN_HEIGHT, SWIMLANE_PADDING_X, SWIMLANE_PADDING_Y } from "../core/constants";
import { processFlowElkPreset } from "./elkLayeredPreset";
/**
 * Lane layout config + storage normalization for the process-flow editor.
 *
 * These two exports encode the same invariant on both axes — children store
 * base coordinates so saved positions survive growth changes across drags —
 * so we bundle them together to make the coupling visible:
 *
 *  - `storage.toStorage` stores a child's absolute X (`parent.x + rf.x` —
 *    the lane sits at `x = -leftGrowth`, so absolute X IS the baseX) and its
 *    base Y (`rf.y - upGrowth`).
 *  - `applyLaneLayout` (keyed on `laneLayout`) re-derives both growths on
 *    every structural derive, so `fromStorage` can be identity — stored base
 *    coordinates land in `node.position` and the lane preset adds the
 *    growths back on the next render.
 *
 * Pass `PROCESS_FLOW_LANE_ADAPTER.laneLayout` to `useEditableFlow` or
 * `useFlow`'s `laneLayout` prop so lane containers resize to match effective
 * child positions, and set `PROCESS_FLOW_LANE_ADAPTER.storage` as the
 * adapter's `storage` field. Re-exports of the individual halves are kept
 * for backwards-compat — new code should prefer the bundle.
 */ export const PROCESS_FLOW_LANE_ADAPTER = {
    laneLayout: {
        isLane: (n)=>n.type === "flowSwimlane",
        getNodeDimensions,
        metrics: {
            labelSize: SWIMLANE_LABEL_WIDTH,
            paddingX: SWIMLANE_PADDING_X,
            paddingY: SWIMLANE_PADDING_Y,
            gap: SWIMLANE_GAP,
            minSize: SWIMLANE_MIN_HEIGHT
        }
    },
    storage: {
        toStorage: (id, rf, ctx)=>{
            const node = ctx.nodeById.get(id);
            const parent = node?.parentId ? ctx.nodeById.get(node.parentId) : null;
            const upGrowth = parent?.data?.upGrowth ?? 0;
            return {
                x: parent ? parent.position.x + rf.x : rf.x,
                y: rf.y - upGrowth
            };
        },
        fromStorage: (_id, stored)=>stored
    }
};
export const PROCESS_FLOW_LANE_LAYOUT = PROCESS_FLOW_LANE_ADAPTER.laneLayout;
/**
 * Where a synthetic node that trails a step (`gateway-X`, `end-X[-branch]`)
 * first appears when it has no saved position: one column right of the step.
 */ export function positionAfterStep(stepPos) {
    return {
        x: stepPos.x + NODE_WIDTH + 60,
        y: stepPos.y
    };
}
export function createProcessFlowEditorAdapter(options) {
    const { registry, handlers } = options;
    const editable = {
        onMove: (updates)=>{
            const positions = updates.map((u)=>({
                    stepId: u.id,
                    x: u.position.x,
                    y: u.position.y
                }));
            void handlers.onSavePositions({
                positions
            });
        }
    };
    return {
        registry,
        layout: processFlowElkPreset,
        storage: PROCESS_FLOW_LANE_ADAPTER.storage,
        editable,
        features: {
            bridges: true
        },
        // Anchor synthetic nodes that proxy a real step (`gateway-X` and
        // `end-X[-branch]`) to that step's saved position when they first
        // materialize — the manager's default sentinel of `{0,0}` would otherwise
        // strand them at the canvas origin (and pile every end node onto the
        // start node's column). `useEditableFlow`'s seed-on-appear effect picks
        // these derived positions up and persists them, so they stop trailing the
        // predecessor on subsequent renders.
        sentinel: (node, getSaved)=>{
            const predecessorId = resolveStepIdFromNodeId(node.id) ?? (node.type === "flowEnd" ? node.data?.predecessorStepId ?? null : null);
            if (predecessorId) {
                const stepPos = getSaved(predecessorId);
                if (stepPos) return positionAfterStep(stepPos);
            }
            // Content-area origin rather than {0,0}: a base of {0,0} sits left of
            // the lane label, so it would drive the global leftGrowth (shifting
            // every lane box leftward) and the seed-on-appear effect would persist
            // it.
            return {
                x: SWIMLANE_LABEL_WIDTH + SWIMLANE_PADDING_X,
                y: SWIMLANE_PADDING_Y
            };
        },
        build (source) {
            const { process, options: assembleOptions } = source;
            const assembly = assembleProcessFlow(process, new Map(), assembleOptions ?? {});
            return {
                nodes: assembly.nodes,
                edges: assembly.edges
            };
        }
    };
}

//# sourceMappingURL=editorAdapter.js.map